/**
 * ScanTemplateSchemaHeuristics.ts
 *
 * Heuristic functions for inferring properties of scan template schema elements
 * using string analysis and other quick "back-of-the-envelope" methods.
 */

import { isArray, isPlainObject } from '@art-suite/art-core-ts';
import { pureFunctionalMap, pureFunctionalMerge, pureFunctionalRecordMap } from '../pureFunctionalLib';
import { SCAN_TEMPLATE_CHILDREN_FIELD_NAMES_BY_DEPTH, SCAN_TEMPLATE_PART_DEPTHS_BY_NAME, SCAN_TEMPLATE_PART_TYPES_BY_DEPTH } from './ScanTemplateDepthLib';
import {
  ScanTemplateGroup,
  ScanTemplateLineItem,
  ScanTemplateOption,
  ScanTemplatePart,
  ScanTemplatePartType,
  ScanTemplateRoot,
  ScanTemplateTask,
} from './ScanTemplateSchemas';

export type MapScanPartMapper = (node: ScanTemplatePart, nodeType: ScanTemplatePartType) => ScanTemplatePart;

/**
 * maps on all children of a scan template part (The depth indicator specifies your scan part type)
 * @param templatePart - the scan template part to map
 * @param mapper - the mapper function to apply to each child
 * @param depth - the depth of the scan template part
 * @returns the mapped scan template part
 */
const _mapScanTemplateParts = <T extends ScanTemplatePart>(
  templatePart: T,
  mapper: MapScanPartMapper,
  depth: number,
): T => {
  const childrenName = SCAN_TEMPLATE_CHILDREN_FIELD_NAMES_BY_DEPTH[depth];
  const children = childrenName ? (templatePart as any)[childrenName] : undefined;

  const newChildren = children ? pureFunctionalMap(children, (child: ScanTemplatePart) => _mapScanTemplateParts(child, mapper, depth + 1)) : undefined;

  const updatedTemplatePart = children !== templatePart ? { ...templatePart, [childrenName]: newChildren } : templatePart;

  return mapper(updatedTemplatePart, SCAN_TEMPLATE_PART_TYPES_BY_DEPTH[depth]) as T;
};

export const mapScanTemplatePartsFromRoot = (
  templateRoot: ScanTemplateRoot,
  mapper: MapScanPartMapper,
): ScanTemplateRoot => {
  const newTaskTemplatesById = isPlainObject(templateRoot.taskTemplatesById)
    ? pureFunctionalRecordMap(
      templateRoot.taskTemplatesById,
      taskTemplate => _mapScanTemplateParts(taskTemplate, mapper, SCAN_TEMPLATE_PART_DEPTHS_BY_NAME.taskTemplate),
    )
    : undefined;
  const newGroupTemplates = isArray(templateRoot.groupTemplates)
    ? pureFunctionalMap(
      templateRoot.groupTemplates,
      groupTemplate => _mapScanTemplateParts(groupTemplate, mapper, SCAN_TEMPLATE_PART_DEPTHS_BY_NAME.groupTemplate),
    )
    : undefined;

  return pureFunctionalMerge(templateRoot, {
    taskTemplatesById: newTaskTemplatesById,
    groupTemplates: newGroupTemplates,
  });
};

export const mapScanTemplateParts = <T extends ScanTemplatePart>(
  templatePart: T,
  mapper: MapScanPartMapper,
  templatePartType: ScanTemplatePartType,
): T => {
  switch (templatePartType) {
    case 'scanTemplate':
      return mapScanTemplatePartsFromRoot(templatePart as ScanTemplateRoot, mapper) as T;
    case 'groupTemplate':
      return _mapScanTemplateParts(templatePart, mapper, SCAN_TEMPLATE_PART_DEPTHS_BY_NAME.groupTemplate);
    case 'taskTemplate':
      return _mapScanTemplateParts(templatePart, mapper, SCAN_TEMPLATE_PART_DEPTHS_BY_NAME.taskTemplate);
    case 'optionTemplate':
      return _mapScanTemplateParts(templatePart, mapper, SCAN_TEMPLATE_PART_DEPTHS_BY_NAME.optionTemplate);
    case 'lineItemTemplate':
      return _mapScanTemplateParts(templatePart, mapper, SCAN_TEMPLATE_PART_DEPTHS_BY_NAME.lineItemTemplate);
    default:
      throw new Error(`Invalid template part type: ${templatePartType}`);
  }
};

export const mapScanTemplatePartsFromGroup = (templateGroup: ScanTemplateGroup, mapper: MapScanPartMapper): ScanTemplateGroup =>
  _mapScanTemplateParts(templateGroup, mapper, SCAN_TEMPLATE_PART_DEPTHS_BY_NAME.groupTemplate);
export const mapScanTemplatePartsFromTask = (templateTask: ScanTemplateTask, mapper: MapScanPartMapper): ScanTemplateTask =>
  _mapScanTemplateParts(templateTask, mapper, SCAN_TEMPLATE_PART_DEPTHS_BY_NAME.taskTemplate);
export const mapScanTemplatePartsFromOption = (templateOption: ScanTemplateOption, mapper: MapScanPartMapper): ScanTemplateOption =>
  _mapScanTemplateParts(templateOption, mapper, SCAN_TEMPLATE_PART_DEPTHS_BY_NAME.optionTemplate);
export const mapScanTemplatePartsFromLineItem = (templateLineItem: ScanTemplateLineItem, mapper: MapScanPartMapper): ScanTemplateLineItem =>
  _mapScanTemplateParts(templateLineItem, mapper, SCAN_TEMPLATE_PART_DEPTHS_BY_NAME.lineItemTemplate);
